import { type TermChangeRow, termValueOn } from 'pages/Loan/Servicing/RateChanges/types'
import { useEffect, useMemo, useState } from 'react'
import { getTermChanges } from 'services'
import { Modal } from 'stories/components'
import {
  calculateNextPaymentDate,
  formatDate,
  formatDateYMD,
  getLoanDisplayNo,
  getPrice2decimal,
  roundToCents,
} from 'utils'

const PERIODS_PER_YEAR: Record<string, number> = {
  daily: 365,
  weekly: 52,
  biweekly: 26,
  monthly: 12,
  bimonthly: 6,
  'semi annualy': 2,
  'semi-annualy': 2,
  annualy: 1,
}

const num = (v: any) => Number(String(v ?? '').replace(/,/g, '')) || 0
const money = (v: number) => getPrice2decimal(v, false, true)

type Row = {
  n: number
  date: string
  rate: number
  payment: number
  principal: number
  interest: number
  balance: number
  /** True on the first payment a scheduled rate or payment change reaches. */
  changed: boolean
}

// Builds an amortization schedule from the current servicing terms (current UPB,
// current interest rate, P&I payment, payment frequency, next payment date, maturity),
// following the loan's Rate & Payment Changes: each payment is figured at the rate and P&I
// scheduled for it, and where a rate change recalculates the payment (Rate Change Payment on
// Servicing Information) the level payment is re-derived over the payments left.
const buildSchedule = (data: Record<string, any>, changes: TermChangeRow[] = [], recast = true) => {
  const balance0 = num(data.currentUpb) || num(data.originalBalance)
  const annualRate = num(data.currentInterestRate)
  const frequency = String(data.paymentFrequency || 'Monthly')
  const periodsPerYear = PERIODS_PER_YEAR[frequency.toLowerCase().trim()] || 12
  const periodRate = annualRate / 100 / periodsPerYear
  const payment = num(data.principalInterest) || num(data.totalPayment)
  const rawStart = data.nextPaymentDate || data.firstPaymentDate || ''
  const startDate = rawStart ? formatDateYMD(rawStart) : ''
  const rawMaturity = data.curMatDate || data.originalMatDate || data.maturityDate || ''
  const maturity = rawMaturity ? formatDateYMD(rawMaturity) : ''

  const meta = { balance0, annualRate, payment, frequency, startDate, maturity, interestOnly: false }

  if (!balance0 || !startDate) {
    return { rows: [] as Row[], meta, error: 'Need a current balance and a next payment date on the servicing info.' }
  }
  const firstInterest = balance0 * periodRate
  const amortizesFirst = payment > firstInterest + 0.005
  if (!maturity && !amortizesFirst) {
    return {
      rows: [] as Row[],
      meta,
      error: 'This looks interest-only — add a maturity date on the servicing info to schedule the balloon.',
    }
  }

  // Payments left from `from` through maturity, for re-deriving a level payment after a rate change.
  const paymentsLeft = (from: string) => {
    if (!maturity) return 0
    let count = 0
    let d = from
    while (d && d <= maturity && count < 600) {
      count++
      d = calculateNextPaymentDate(d, frequency)
    }
    return count
  }

  const MAX = 600
  const rows: Row[] = []
  let balance = balance0
  let date = startDate
  let rate = annualRate
  let currentPayment = payment
  for (let i = 0; i < MAX && balance > 0.01; i++) {
    const scheduledRate = termValueOn(changes, 'rate', date)
    const scheduledPayment = termValueOn(changes, 'payment', date)
    const nextRate = scheduledRate ?? annualRate
    const rateMoved = i > 0 && nextRate !== rate
    // Amortizing means the payment was retiring principal at the rate before the change; an
    // interest-only loan's payment is its interest and is never recalculated into principal.
    // `roundToCents`, not the file-local `Math.round(v * 100) / 100` this replaced: a balance times
    // a period rate lands on an exact half cent constantly, and rounding the binary double quotes
    // it a cent light. Here that cent decides whether the row counts as amortizing at all, so it
    // is not only a display difference.
    const wasAmortizing = currentPayment > roundToCents(balance * (rate / 100 / periodsPerYear)) + 0.005
    rate = nextRate
    const rowPeriodRate = rate / 100 / periodsPerYear
    if (scheduledPayment != null) {
      currentPayment = scheduledPayment
    } else if (rateMoved && recast && wasAmortizing && currentPayment > 0) {
      const n = paymentsLeft(date)
      if (n > 0 && rowPeriodRate > 0) {
        currentPayment = roundToCents((balance * rowPeriodRate) / (1 - Math.pow(1 + rowPeriodRate, -n)))
      }
    }
    const changed = i > 0 && (rateMoved || (scheduledPayment != null && scheduledPayment !== rows[i - 1]?.payment))
    // The period's interest: $27,000 at 10.99% monthly is exactly $247.275, which the old helper
    // billed as $247.27 against the $247.28 the loan is billed. Principal is the plug for whatever
    // interest leaves behind, so the stray cent landed there.
    const interest = roundToCents(balance * rowPeriodRate)
    const nextDate = calculateNextPaymentDate(date, frequency)
    const isLast = maturity ? !nextDate || nextDate > maturity : false
    const amortizes = currentPayment > interest + 0.005
    let principal: number
    if (isLast) {
      principal = balance // final payment pays off the remaining balance (balloon if interest-only)
      if (!amortizes) meta.interestOnly = true
    } else if (amortizes) {
      principal = Math.min(roundToCents(currentPayment - interest), balance)
    } else {
      principal = 0
      meta.interestOnly = true
    }
    principal = Math.min(principal, balance)
    const pay = roundToCents(interest + principal)
    balance = roundToCents(balance - principal)
    rows.push({
      n: i + 1,
      date,
      rate,
      payment: pay,
      principal: roundToCents(principal),
      interest,
      balance: Math.max(balance, 0),
      changed,
    })
    if (isLast || !nextDate) break
    date = nextDate
  }

  return { rows, meta, error: '' }
}

export const AmortizationScheduleModal = ({
  data,
  loanNumber,
  byteproFileName,
  onClose,
}: {
  data: Record<string, any>
  loanNumber: number
  byteproFileName?: number
  onClose: () => void
}) => {
  // The loan's Rate & Payment Changes, so the projection follows what is scheduled.
  const [changes, setChanges] = useState<TermChangeRow[]>([])
  const [recast, setRecast] = useState(true)
  useEffect(() => {
    getTermChanges()
      .then((res: any) => {
        setChanges(res?.changes || [])
        setRecast(res?.settings?.rateChangeRecast !== 'keep')
      })
      .catch(() => setChanges([]))
  }, [])
  const { rows, meta, error } = useMemo(() => buildSchedule(data, changes, recast), [data, changes, recast])
  const scheduledCount = changes.filter((c) => c.status === 'scheduled').length

  const totals = useMemo(
    () =>
      rows.reduce(
        (a, r) => ({ payment: a.payment + r.payment, principal: a.principal + r.principal, interest: a.interest + r.interest }),
        { payment: 0, principal: 0, interest: 0 },
      ),
    [rows],
  )

  const onDownloadCsv = () => {
    const header = ['#', 'Due Date', 'Rate', 'Payment', 'Principal', 'Interest', 'Balance']
    const lines = rows.map((r) => [
      r.n,
      r.date,
      r.rate,
      r.payment.toFixed(2),
      r.principal.toFixed(2),
      r.interest.toFixed(2),
      r.balance.toFixed(2),
    ])
    const csv = [header, ...lines].map((row) => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `amortization-${getLoanDisplayNo({ byteproFileName, loanNumber })}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Modal title="Amortization Schedule" titleOkay="" isOpen onClose={onClose}>
      <div className="w-full max-w-[820px]">
        {/* Assumptions */}
        <div className="mb-3 grid grid-cols-1 gap-x-6 gap-y-1 rounded-md bg-slate-50 p-3 text-[13px] sm:grid-cols-2 md:grid-cols-3">
          <div>
            <span className="text-gray-500">Beginning Balance:</span> <span className="font-semibold">${money(meta.balance0)}</span>
          </div>
          <div>
            <span className="text-gray-500">Interest Rate:</span> <span className="font-semibold">{getPrice2decimal(meta.annualRate, false, true)}%</span>
          </div>
          <div>
            <span className="text-gray-500">P&amp;I Payment:</span> <span className="font-semibold">${money(meta.payment)}</span>
          </div>
          <div>
            <span className="text-gray-500">Frequency:</span> <span className="font-semibold">{meta.frequency}</span>
          </div>
          <div>
            <span className="text-gray-500">First Due:</span> <span className="font-semibold">{meta.startDate ? formatDate(meta.startDate) : '—'}</span>
          </div>
          <div>
            <span className="text-gray-500">Maturity:</span> <span className="font-semibold">{meta.maturity ? formatDate(meta.maturity) : '—'}</span>
          </div>
        </div>

        {error ? (
          <div className="py-8 text-center text-sm text-rose-600">{error}</div>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[12px] text-gray-500">
                {rows.length} payment{rows.length === 1 ? '' : 's'}
                {meta.interestOnly ? ' · interest-only with balloon at maturity' : ''}
                {scheduledCount
                  ? ` · follows ${scheduledCount} scheduled change${scheduledCount === 1 ? '' : 's'} (Rate & Payment Changes)`
                  : ''}
              </span>
              <button
                type="button"
                onClick={onDownloadCsv}
                className="rounded-md border border-slate-200 px-3 py-1 text-[13px] text-shade-blue hover:bg-slate-50"
              >
                Download CSV
              </button>
            </div>
            <div className="max-h-[55vh] overflow-auto rounded-md border border-slate-200">
              <table className="w-full text-right text-[13px]">
                <thead className="sticky top-0 bg-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Due Date</th>
                    <th className="px-3 py-2">Rate</th>
                    <th className="px-3 py-2">Payment</th>
                    <th className="px-3 py-2">Principal</th>
                    <th className="px-3 py-2">Interest</th>
                    <th className="px-3 py-2">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.n}
                      className={`border-b border-slate-100 ${
                        r.changed ? 'bg-amber-50' : r.n % 2 === 0 ? 'bg-slate-50/50' : ''
                      }`}
                    >
                      <td className="px-3 py-1.5 text-left text-slate-500">{r.n}</td>
                      <td className="px-3 py-1.5 text-left">{formatDate(r.date)}</td>
                      <td className="px-3 py-1.5">{getPrice2decimal(r.rate, false, true)}%</td>
                      <td className="px-3 py-1.5">${money(r.payment)}</td>
                      <td className="px-3 py-1.5">${money(r.principal)}</td>
                      <td className="px-3 py-1.5">${money(r.interest)}</td>
                      <td className="px-3 py-1.5 font-medium">${money(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 bg-slate-100 font-semibold">
                  <tr>
                    <td className="px-3 py-2 text-left" colSpan={3}>
                      Totals
                    </td>
                    <td className="px-3 py-2">${money(totals.payment)}</td>
                    <td className="px-3 py-2">${money(totals.principal)}</td>
                    <td className="px-3 py-2">${money(totals.interest)}</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
